// src/utils/membershipJoin.js
// Membership join v2 and email confirmation on the client
// (STEP3_5A_TIERS_DESIGN.md 3.13.2, 3.13.3, 5.16 and 5.17; release R6).
// Team joins (evergreen 3.5b, STEP3_5B_MERGE_DELTA.md section 6) add the
// team J0 title (W1), the team J10 lead (W2) and the team L10a / L10b (W3,
// W4); every channel string stays word for word.
//
// Display only. The server decides every join: getMembershipOffer says
// whether an unverified payer must confirm an email first (emailGate) or
// wait (waitGate); createMembershipCheckout says which payment step to show
// (flow 'element' = our page with Stripe's Payment Element, flow 'checkout'
// = Stripe's Embedded Checkout as before); confirmMembershipJoin checks the
// card before anything is charged and returns a status. When the bank wants
// to confirm the payment, nothing is charged and the same join continues on
// Embedded Checkout, where Stripe runs the bank's check (founder decision
// 10-18; this page never runs it). This module holds
// the R6 fan strings word for word (5.17), the masked address the panels
// show (streamers capture their screens live, so a panel never shows a full
// address), J10's wording rules, the confirm-email route's return target,
// and how a confirm result or refusal maps to what the join panel shows.
//
// "Confirm" is for email, "verify" only for ID and age (the only thing that
// lifts the $500 limit). Copy rules: no em dashes, never "AI", paid members
// are "members". ASCII only (the bullets of a masked address are \u2022).
//
// Zero imports: unit-tested in plain node
// (web-landing/scripts/test-membership-join-v2.mjs). The verify clause of J4
// and J10 follows the account's verify state through spendingLimit.js
// (withVerify / verifyLine), composed by the caller.

export const SUPPORT_EMAIL = 'support@critcash.com';

// 499 -> "$4.99" (the server's usd(); J3 needs the plain amount, since the
// monthly formatter already adds "a month").
export function formatUsd(cents) {
  return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
}

const isStr = (v) => typeof v === 'string' && v.trim().length > 0;

// ---------------------------------------------------------------------------
// The payment step (J0 to J5L, and the bank-check continuation)
// ---------------------------------------------------------------------------

// J0: the payment step's title names what is being bought (the disclosure
// names neither the tier nor the creator). Without a tier name (a legacy
// one-price checkout) it reads "Membership to {name}'s channel".
export function payStageTitle(tierName, name) {
  return isStr(tierName) ? `${tierName} membership to ${name}'s channel` : `Membership to ${name}'s channel`;
}

// W1 (evergreen 3.5b; a team join only): J0 for a team. A team has one price
// and no tiers, so the title names the team alone. teamName is the rendered
// noun ("Team X": membershipPayment.js teamDisplayName).
export const teamPayStageTitle = (teamName) => `Membership to ${teamName}`;

// J1: the join panel footer, every payer (the descriptor the tier Products
// carry; "should": a bank may show an enriched merchant name instead).
export const STATEMENT_LINE = 'Your card statement should show CRITCASH MEMBER.';

// J3: the pay button.
export const payButtonLabel = (cents) => `Join for ${formatUsd(cents)} a month`;

// J4 (raw; withVerify drops ", or verify your account to remove the limit."
// for the support and in-review states and adds L11s / L11r).
export const JOIN_CARD_LIMIT_LINE = "This card can't be used for that membership on an unverified account right now. Nothing was charged. Try another card, or verify your account to remove the limit.";
// J5
export const JOIN_CARD_BLOCKED_LINE = `This card can't be used for Super Crits, Rumbles or new memberships on CritCast. Nothing was charged. Please use a different card. If you think this is a mistake, contact ${SUPPORT_EMAIL}.`;
// J5a: the server's confirm came back declined for authentication
// (authentication_failed).
export const BANK_NOT_CONFIRMED_LINE = "Your bank didn't confirm this payment. Nothing was charged. Try again, or use another card.";
// J5d: Stripe's own decline message as returned, then this.
export const DECLINE_TAIL = 'Nothing was charged. Try another card.';
// J5L
export const NON_CARD_LINE = "This payment method can't be used for memberships. Nothing was charged. Please use a card.";
// The bank wants to confirm the payment (approved by the founder 10-18):
// nothing was charged, and the same join continues on Stripe's secure
// payment page (Embedded Checkout), shown before and while switching.
export const BANK_CONTINUE_LINE = 'Your bank wants to confirm this payment. Continue to the secure payment page to finish joining. Nothing was charged.';

export const TRY_AGAIN_LABEL = 'Try again';
export const USE_ANOTHER_CARD_LABEL = 'Use another card';

// J5d. A message without closing punctuation gets a period.
export function declineLine(message) {
  let m = isStr(message) ? message.trim() : '';
  if (m && !/[.!?]$/.test(m)) m += '.';
  return m ? `${m} ${DECLINE_TAIL}` : DECLINE_TAIL;
}

// ---------------------------------------------------------------------------
// The email branch (J6 to J9w), the wait (J10)
// ---------------------------------------------------------------------------

// J6, the server's sentence (old bundles show it as is; a new panel reloads
// the offer and lands on J7).
export const EMAIL_SERVER_SENTENCE = 'membership-email: Confirm your email to join channel memberships.';
// S1 (evergreen 3.5b): J6's team twin, the server's refusal of a team join.
// The panel never shows it either (it reloads the offer and lands on J7).
export const TEAM_EMAIL_SERVER_SENTENCE = 'membership-email: Confirm your email to join team memberships.';

// J7 (in place of the consent box and "Continue to payment").
export function emailGateLead(maskedEmail) {
  return isStr(maskedEmail)
    ? `Confirm your email to join. We'll send a link to ${maskedEmail}.`
    : 'Confirm your email to join.';
}
export const SEND_CONFIRMATION_LABEL = 'Send confirmation email';
export const NOT_NOW_LABEL = 'Not now';

// J8 (after sending).
export const emailSentLine = (maskedEmail) => `We sent a link to ${maskedEmail}. Open it, then come back here. If you don't see it, check your spam folder.`;
export const CONFIRMED_IT_LABEL = "I've confirmed it";
export const SEND_AGAIN_LABEL = 'Send it again';

// J9 ("I've confirmed it" tapped while still not confirmed).
export const notConfirmedYetLine = (maskedEmail) => `Your email isn't confirmed yet. Open the link we sent to ${maskedEmail}, or send a new one.`;

// J9r (the send limit; cooldown = formatCooldown, "42m" or "5h 20m").
export const sendLimitLine = (cooldown) => `You've asked for a few links already. Try again in ${cooldown}.`;

// J9w (under J7 and J8).
export const WRONG_ADDRESS_LINE = `Wrong address? Contact ${SUPPORT_EMAIL}.`;

// An account with no email address at all (the server's email-missing
// sentence, word for word): in place of J9w, which asks about a wrong one.
// R6 copy read 13.2 V2, the default applied 10-18.
export const NO_ADDRESS_LINE = `Your account has no email address. Contact ${SUPPORT_EMAIL}.`;

// The panel re-checks on its own after a send (3.13.3): on window focus, and
// every 5 seconds for 2 minutes.
export const EMAIL_POLL_EVERY_MS = 5000;
export const EMAIL_POLL_FOR_MS = 2 * 60 * 1000;

// The longest wait call D allows (Terms v1.6 section 6: "up to 7 days").
export const MAX_WAIT_HOURS = 168;

/**
 * J10's `${ageText}` (5.17), the server's PD.ageText word for word: '1 hour'
 * when X is 1; '${X} hours' when X is under 48 or not a multiple of 24;
 * otherwise '${X/24} days'.
 */
export function ageText(hours) {
  const n = Math.floor(Number(hours));
  const x = Number.isFinite(n) ? Math.min(MAX_WAIT_HOURS, Math.max(0, n)) : 0;
  if (x === 1) return '1 hour';
  if (x < 48 || x % 24 !== 0) return `${x} hours`;
  return `${x / 24} days`;
}

// J10's lead: the channel sentence, or W2 for a team join (evergreen 3.5b;
// the team twin of the server's S2). Any kind other than 'team' is a channel.
function waitLead(hours, kind) {
  return kind === 'team'
    ? `New accounts can join team memberships ${ageText(hours)} after signing up.`
    : `New accounts can join channel memberships ${ageText(hours)} after signing up.`;
}

// J10 server sentence (old bundles show it as is); kind 'team' = S2.
export const waitServerSentence = (hours, kind = 'channel') => `membership-wait: ${waitLead(hours, kind)}`;

// `${dateTime}`: the viewer's local date and time with the time zone.
export function joinAtText(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Date(n).toLocaleString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });
}

// J10's support and in-review endings (R6 copy read 12.2 item 3, approved
// 10-18): the limit lines' L11s / L11r sentences talk about the limit, not
// the wait, so the wait has its own two sentences.
export const WAIT_SUPPORT_SENTENCE = `Contact ${SUPPORT_EMAIL} to finish your age check and skip the wait.`;
export const WAIT_IN_REVIEW_SENTENCE = "Your verification is being reviewed; once it's approved you can join right away.";

/**
 * J10 panel line. v = spendingLimit.verifyLine(limit.verify): for 'start'
 * the line keeps its own verify clause and the "Verify your ID and age"
 * button; 'support' and 'in_review' replace the clause with the wait's own
 * sentence (WAIT_SUPPORT_SENTENCE with the "Contact support" button, or
 * WAIT_IN_REVIEW_SENTENCE with none). kind 'team' (evergreen 3.5b, a team
 * join) swaps the lead for W2; the tails are the same approved sentences.
 * -> { id, text, button }.
 */
export function waitPanelLine({ hours, dateTime, verify, kind } = {}) {
  const lead = waitLead(hours, kind);
  const v = verify && typeof verify === 'object' ? verify : null;
  if (v && v.kind === 'support') {
    return { id: 'J10', text: `${lead} You can join after ${dateTime}. ${WAIT_SUPPORT_SENTENCE}`, button: v.button || null };
  }
  if (v && v.kind === 'in_review') {
    return { id: 'J10', text: `${lead} You can join after ${dateTime}. ${WAIT_IN_REVIEW_SENTENCE}`, button: null };
  }
  return { id: 'J10', text: `${lead} You can join after ${dateTime}, or verify your ID and age to skip the wait.`, button: v ? v.button || null : null };
}

// ---------------------------------------------------------------------------
// Verification Center: the Email row (J7v)
// ---------------------------------------------------------------------------

export const EMAIL_ROW = Object.freeze({
  title: 'Email',
  confirmed: 'Confirmed',
  notConfirmed: 'Not confirmed',
  // R6 copy read 12.3 note 7 (approved 10-18): "go here" was vague in a status row.
  body: 'Needed to join channel memberships on an unverified account. Membership receipts and renewal reminders are sent to this address.',
  button: SEND_CONFIRMATION_LABEL,
});

// The row's line after a send. Not in 5.17 (J8 says "come back here", which
// fits the join panel only): the R6 copy read's suggested default (12.2
// item 4), with J9w under it and J9r for the send limit.
export const verificationSentLine = (maskedEmail) => `We sent a link to ${maskedEmail}. If you don't see it, check your spam folder.`;

// ---------------------------------------------------------------------------
// The confirm-email page (J12, J12x)
// ---------------------------------------------------------------------------

export const CONFIRMED_LINE = 'Your email is confirmed.';
export const CAN_JOIN_NOW_LINE = 'You can join channel memberships now.';
// J12. The second sentence only when memberships are live and the wait
// (call D) is known not to apply to the account (R6 copy read 12.3 note 1:
// while memberships are dark it would be false); otherwise the first
// sentence alone, followed by J10's panel line when the wait applies.
export const confirmedText = (canJoinNow) => (canJoinNow ? `${CONFIRMED_LINE} ${CAN_JOIN_NOW_LINE}` : CONFIRMED_LINE);
export const BACK_TO_CRITCAST_LABEL = 'Back to CritCast';
// J12x
export const LINK_USED_LINE = 'This link has expired or was already used. You can send a new one from Verification Center.';
export const OPEN_VERIFICATION_LABEL = 'Open Verification Center';
// The page title, the loading line and a failure that is not an expired or
// used link. Not in 5.17: the R6 copy read's suggested defaults (12.2 item 5).
export const CONFIRM_PAGE_TITLE = 'Confirm your email';
export const CONFIRMING_LINE = 'Confirming your email...';
export const CONFIRM_FAILED_LINE = "We couldn't confirm your email right now. Try the link again in a moment.";

// The server's allowlisted channel names (PD.CHANNEL_NAME_RE): the only
// names the emailed link may carry as ch.
export const CHANNEL_NAME_RE = /^[A-Za-z0-9_.-]{1,64}$/;

/**
 * Where "Back to CritCast" goes (the client mirror of PD.returnUrlFor):
 * to=join with a valid ch -> the channel page (StreamWatch {channel});
 * anything else -> Verification Center. Never a URL from the link.
 */
export function confirmEmailTarget(params) {
  const p = params && typeof params === 'object' ? params : {};
  const ch = typeof p.ch === 'string' ? p.ch : '';
  if (p.to === 'join' && CHANNEL_NAME_RE.test(ch)) return { route: 'StreamWatch', params: { channel: ch } };
  return { route: 'Verification', params: undefined };
}

// applyActionCode's refusal -> 'used' (J12x: an expired, used or unknown
// code, or an account that no longer exists) or 'retry' (anything else:
// the network, a rate limit).
const USED_CODES = new Set([
  'auth/expired-action-code', 'auth/invalid-action-code', 'auth/user-not-found', 'auth/user-disabled',
]);
export function applyCodeErrorKind(err) {
  return USED_CODES.has(err && err.code) ? 'used' : 'retry';
}

// R6 review R6C-3: whether a 'used' refusal may still mean the link's own
// account is confirmed, so the page may show J12 from the signed-in account.
// Only an already-used code (the same link opened twice, or a mail scanner
// that opened it first) does; an expired code or a gone account never
// confirmed anything, and the signed-in account may be another one.
export function usedCodeMayBeConfirmed(err) {
  return (err && err.code) === 'auth/invalid-action-code';
}

// True when no wait can apply to an account created at creationTimeMs: it
// is older than the longest wait call D allows. Unknown -> false.
export function waitCannotApply(creationTimeMs, nowMs = Date.now()) {
  const c = Number(creationTimeMs);
  return Number.isFinite(c) && c > 0 && nowMs - c >= MAX_WAIT_HOURS * 60 * 60 * 1000;
}

// ---------------------------------------------------------------------------
// The masked address (the server's PD.maskEmail word for word)
// ---------------------------------------------------------------------------

/**
 * The first character, three bullets (U+2022), '@' and the domain
 * ('d', three bullets, '@gmail.com'); null without a usable address.
 */
export function maskEmail(email) {
  if (typeof email !== 'string') return null;
  const s = email.trim();
  const at = s.lastIndexOf('@');
  if (at < 1 || at === s.length - 1) return null;
  return `${s[0]}\u2022\u2022\u2022@${s.slice(at + 1)}`;
}

// ---------------------------------------------------------------------------
// What the payment step does with a result
// ---------------------------------------------------------------------------

/**
 * confirmMembershipJoin's result -> what the payment step does:
 *  - { kind: 'welcome' }: succeeded or processing (the webhook records the
 *    payment and the welcome waits on the pair doc), or refused (step 4b
 *    refunded a card it refused after the charge; the welcome shows L10a /
 *    L10b from the pair doc);
 *  - { kind: 'continue_checkout', text }: the bank wants to confirm the
 *    payment (continue_checkout; requires_action reads the same). Nothing
 *    was charged and the server closed this payment step: the join window
 *    shows BANK_CONTINUE_LINE and the same join continues on Embedded
 *    Checkout under it (createMembershipCheckout, flow 'checkout');
 *  - { kind: 'line', id: 'J5a' | 'J5d', text, tryAgain, anotherCard };
 *  - { kind: 'ready' }: nothing reached Stripe (requires_payment_method) or
 *    an unknown status: the pay button is back, nothing to say.
 */
export function joinResultAction(res) {
  const s = res && typeof res === 'object' ? res.status : null;
  if (s === 'succeeded' || s === 'processing' || s === 'refused') return { kind: 'welcome' };
  if (s === 'continue_checkout' || s === 'requires_action') return { kind: 'continue_checkout', text: BANK_CONTINUE_LINE };
  if (s === 'declined') {
    if (res.reason === 'authentication_failed') {
      return { kind: 'line', id: 'J5a', text: BANK_NOT_CONFIRMED_LINE, tryAgain: true, anotherCard: true };
    }
    return { kind: 'line', id: 'J5d', text: declineLine(res.message), tryAgain: false, anotherCard: true };
  }
  return { kind: 'ready' };
}

// R6 review R6C-BC-3: once the bank wanted to confirm a join payment, a
// reopened join for the same account and channel asks for Embedded Checkout
// directly for the checkout's lifetime (35 min), so the same card does not
// bounce again. Kept in memory for this page load only (no storage). A team
// join (3.5b) passes 'team:' + teamId as the second key, so a team never
// shares a memory with a channel uid.
export const BANK_ASK_MEMORY_MS = 35 * 60 * 1000;
const bankAsks = new Map();
export function rememberBankAsk(uid, creatorUid, nowMs = Date.now()) {
  if (isStr(uid) && isStr(creatorUid)) bankAsks.set(`${uid}_${creatorUid}`, nowMs);
}
export function bankAskedRecently(uid, creatorUid, nowMs = Date.now()) {
  const at = isStr(uid) && isStr(creatorUid) ? bankAsks.get(`${uid}_${creatorUid}`) : undefined;
  return typeof at === 'number' && nowMs - at >= 0 && nowMs - at < BANK_ASK_MEMORY_MS;
}

/**
 * A refusal's kind (membershipErrorKind) on the payment step -> the card
 * line it shows, raw (J4 still goes through withVerify), or null.
 * -> { id: 'J4' | 'J5' | 'J5L', text, verifyClause } | null.
 */
export function cardRefusalLine(kind) {
  if (kind === 'card_limit') return { id: 'J4', text: JOIN_CARD_LIMIT_LINE, verifyClause: true };
  if (kind === 'card_blocked') return { id: 'J5', text: JOIN_CARD_BLOCKED_LINE, verifyClause: false };
  if (kind === 'card_noncard') return { id: 'J5L', text: NON_CARD_LINE, verifyClause: false };
  return null;
}

/**
 * W3 / W4 (evergreen 3.5b; a team join refused for its card, on the welcome
 * stage): spendingLimit.js L10a / L10b with "Your membership to {name}'s
 * channel" read "Your membership to {Team X}" and nothing else changed.
 * teamName: the rendered noun (teamDisplayName); price and credit: already
 * formatted (spendingLimit.formatCurrency); credit absent or '' = no credit
 * clause; last4 absent = "the card you used". Raw, like cardRefusalLine: the
 * caller sends L10a (verifyClause true) through spendingLimit.withVerify.
 * -> { id: 'L10a' | 'L10b', text, verifyClause } | null.
 */
export function teamRefundLine({ reason, teamName, last4, price, credit } = {}) {
  const card = isStr(last4) ? `the card you used (ending ${last4})` : 'the card you used';
  const creditClause = isStr(credit) ? `, and returned ${credit} to your account credit` : '';
  const refunded = `We refunded your payment of ${price} in full${creditClause}; the refund usually shows within 5 to 10 business days, or the charge drops off your statement.`;
  if (reason === 'card_limit') {
    return {
      id: 'L10a',
      text: `Your membership to ${teamName} didn't start because ${card} can't be used for that membership on an unverified account right now. ${refunded} Verify your account to remove the limit.`,
      verifyClause: true,
    };
  }
  if (reason === 'card_blocked') {
    return {
      id: 'L10b',
      text: `Your membership to ${teamName} didn't start because ${card} can't be used for new memberships on CritCast. ${refunded} If you think this is a mistake, contact ${SUPPORT_EMAIL}.`,
      verifyClause: false,
    };
  }
  return null;
}

// The offer's gates, as the panel reads them: { email, wait } (each null
// when absent). email: {maskedEmail, sendCooldownMs}; wait: {joinAtMs,
// minAccountAgeHours} while joinAtMs is still ahead. An unavailable offer
// carries neither (the server computes them for an available one only).
export function joinGatesOf(offer, nowMs = Date.now()) {
  const o = offer && typeof offer === 'object' ? offer : {};
  const eg = o.emailGate && typeof o.emailGate === 'object' && o.emailGate.required === true ? o.emailGate : null;
  const wg = o.waitGate && typeof o.waitGate === 'object' ? o.waitGate : null;
  const joinAtMs = Number(wg && wg.joinAtMs);
  return {
    email: eg ? {
      maskedEmail: isStr(eg.maskedEmail) ? eg.maskedEmail : null,
      sendCooldownMs: Math.max(0, Number(eg.sendCooldownMs) || 0),
    } : null,
    wait: wg && Number.isFinite(joinAtMs) && joinAtMs > nowMs ? {
      joinAtMs,
      minAccountAgeHours: Math.max(0, Number(wg.minAccountAgeHours) || 0),
    } : null,
  };
}
